import React, { useState } from 'react';
import { FileText, HelpCircle, Download } from 'lucide-react';
import { getSearchResultsById } from '../services/searchService';
import { getCaseBySearchId, getReviewStatusesForCase } from '../services/caseService';
import { generateCaseCompletionPDF } from '../utils/pdfGenerator';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Button to download a case completion report PDF
 * @param {Object} props - Component props
 * @param {string} props.caseId - The ID of the search results (searchId)
 * @param {string} props.caseStatus - The current status of the case (pending, partially-reviewed, completed)
 * @returns {JSX.Element} The report button component
 */
const CaseReportButton = ({ caseId, caseStatus }) => {
  const { currentUser, currentOrg } = useAuth();
  const { language } = useLanguage();
  const t = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  
  // Determine if button should be enabled
  const isCompleted = caseStatus === 'completed';
  
  // Handle the download action
  const handleDownload = async () => {
    if (!caseId || !currentUser || !currentOrg || !isCompleted) return;
    
    try {
      setIsLoading(true);
      
      // Get both the search results data and the case data with review statuses
      const [searchData, caseData] = await Promise.all([
        getSearchResultsById(caseId, currentOrg?.id),
        getCaseBySearchId(caseId, currentOrg?.id)
      ]);
      
      if (!searchData) {
        console.error('Could not fetch search results');
        setIsLoading(false);
        return;
      }
      
      if (!caseData) {
        console.error('Could not fetch case data');
        setIsLoading(false);
        return;
      }
      
      // Get the review statuses for the case
      const reviewStatuses = caseData.reviewStatuses || {};
      console.log('Review statuses for case:', reviewStatuses);
      
      // Merge search results with review statuses
      const resultsWithReviewStatus = searchData.results.map(result => ({
        ...result,
        review_status: reviewStatuses[result.id]?.review_status || 'pending',
        review_notes: reviewStatuses[result.id]?.review_notes || '',
        review_timestamp: reviewStatuses[result.id]?.review_timestamp || '',
        reviewer_userID: reviewStatuses[result.id]?.reviewer_userID || ''
      }));
      
      console.log('Results with review status:', resultsWithReviewStatus.map(r => ({
        id: r.id,
        name: r.fullName,
        review_status: r.review_status
      })));
      
      // Helper function to safely get a valid date
      const safeDate = (dateValue) => {
        if (!dateValue) return new Date();
        if (dateValue instanceof Date) return isNaN(dateValue.getTime()) ? new Date() : dateValue;
        const parsed = new Date(dateValue);
        return isNaN(parsed.getTime()) ? new Date() : parsed;
      };

      // Prepare the data needed for PDF generation
      const caseDataForPDF = {
        id: searchData.id,
        searchTerm: searchData.searchTerm,
        matches: resultsWithReviewStatus.map(result => ({
          ...result,
          // Ensure source and type are properly passed through
          source: result.source || result.listSource || result.list || result.database || '',
          type: result.type || result.entityType || result.entity_type || (result.name_type === 'company' ? 'Entity' : 'Individual'),
          // Preserve docData if it exists
          docData: result.docData || null
        })),
        matchReviews: resultsWithReviewStatus.map(result => ({
          matchId: result.id,
          nameIndex: result.nameIndex || 0,
          status: result.review_status === 'dismissed' ? 'cleared' : (result.review_status === 'confirmed' ? 'flagged' : 'pending'),
          notes: result.review_notes || '',
          reviewerId: result.reviewer_userID || '',
          reviewerName: result.reviewer_name || currentUser.displayName || '',
          reviewerEmail: result.reviewer_email || currentUser.email || '',
          reviewedAt: result.review_timestamp ? safeDate(result.review_timestamp) : new Date()
        })),
        createdAt: safeDate(caseData.createdAt || searchData.timestamp),
        verificationId: caseData.verificationId || searchData.verificationId || searchData.stats?.verificationId || '',
        runBy: currentUser.displayName || '',
        runByEmail: currentUser.email || '',
        organizationName: currentOrg?.name || ''
      };
      
      console.log('Case data for PDF generation:', {
        totalMatches: caseDataForPDF.matches.length,
        confirmedCount: caseDataForPDF.matchReviews.filter(r => r.status === 'flagged').length,
        dismissedCount: caseDataForPDF.matchReviews.filter(r => r.status === 'cleared').length,
        pendingCount: caseDataForPDF.matchReviews.filter(r => r.status === 'pending').length
      });
      
      // Generate the PDF with the data
      const fileName = `sanctions-report-${caseDataForPDF.searchTerm.replace(/\s+/g, '-')}.pdf`;
      const result = await generateCaseCompletionPDF(caseDataForPDF, fileName, language);
      
      if (!result) {
        console.error('Error generating PDF');
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error generating case report:', error);
      setIsLoading(false);
    }
  };
  
  return (
    <div className="inline-flex items-center">
      <button
        onClick={handleDownload}
        disabled={!isCompleted || isLoading}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
          isCompleted 
            ? 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800' 
            : 'bg-gray-200 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500 opacity-60 border border-gray-300 dark:border-gray-600'
        }`}
        title={isCompleted ? t('cases.report.caseCompleted') : t('cases.report.casePending')}
      >
        {isLoading ? (
          <div className="w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin"></div>
        ) : (
          <FileText size={16} />
        )}
        {t('cases.report.downloadButton')}
      </button>
      
      {/* Info tooltip */}
      <div className="relative ml-1 group">
        <HelpCircle size={16} className="text-gray-400 cursor-help" />
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-60 bg-gray-800 text-white text-xs p-2 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
          {t('cases.report.tooltip')}
        </div>
      </div>
    </div>
  );
};

export default CaseReportButton; 